// src/pages/CreateBookRelease.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';

function CreateBookRelease() {
  const { bookId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [name, setName] = useState(''); // ← NEW: release name
  const [description, setDescription] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [bookName, setBookName] = useState(''); // ← NEW: fetched book name
  // state for a local preview URL (to open selected PDF)
  const [coverPdfPreviewUrl, setCoverPdfPreviewUrl] = useState('');

  const [releaseDate, setReleaseDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  // URL inputs
  const [coverUrl] = useState('');
  const [pdfUrl] = useState('');

  // File inputs
  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  // Previews / UI
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const handleCoverFileChange = (e) =>
    setCoverFile(e.target.files?.[0] || null);

  // Prefill language from ?plang
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const plang = searchParams.get('plang');
    if (plang) setLanguageCode(plang);
  }, [location.search]);

  // When a cover file is picked, create an object URL so user can open it
  useEffect(() => {
    if (coverFile) {
      const url = URL.createObjectURL(coverFile);
      setCoverPdfPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setCoverPdfPreviewUrl('');
  }, [coverFile]);


  useEffect(() => {
    if (!bookId) return;
    axios
      .get(`/api/book/${bookId}`)
      .then(({ data }) => setBookName(data?.name || ''))
      .catch((err) => {
        console.error('Failed to fetch book name:', err);
        setBookName('');
      });
  }, [bookId]);

  const handlePdfFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setPdfFile(file);
  };

  const validate = () => {
    if (!bookId) return 'Missing book id.';
    if (!languageCode) return 'Please choose a language profile.';
    if (!releaseDate) return 'Please select a release date.';
    // Both Cover and Release PDF are optional, validate formats only if provided
    if (coverUrl && !coverUrl.toLowerCase().endsWith('.pdf'))
      return 'Cover URL must end with .pdf';
    if (pdfUrl && !pdfUrl.toLowerCase().endsWith('.pdf'))
      return 'Release PDF URL must end with .pdf';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');

    const error = validate();
    if (error) {
      setMsg(error);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      const finalName = (name && name.trim()) || bookName || ''; // ← fallback to bookName

      formData.append('book_id', bookId);
      formData.append('language_code', languageCode);
      formData.append('release_date', releaseDate); // YYYY-MM-DD is fine
      formData.append('name', finalName);
      formData.append('description', description.trim());

      // Cover: file first, else URL
      if (coverFile) {
        formData.append('cover', coverFile);
      } else if (coverUrl) {
        formData.append('cover_url', coverUrl);
      }

      // PDF: file first, else URL
      if (pdfFile) {
        formData.append('pdf', pdfFile);
      } else if (pdfUrl) {
        formData.append('pdf_url', pdfUrl);
      }

      await axios.post('/api/create-book-release', formData);
      setMsg('Release created successfully.');

      setTimeout(() => {
        navigate(`/book-releases/${bookId}?plang=${languageCode}`);
      }, 600);
    } catch (err) {
      console.error(err);
      setMsg('Error creating the release. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
      <h2 className="text-2xl font-bold text-center mb-4">
        Create a New Release
      </h2>

      {msg && <p className="text-center text-red-500 mb-4">{msg}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Release name (TOP) */}
        <div>
          <label className="block text-gray-700 font-medium">
            Release name
          </label>
          <input
            type="text"
            placeholder={bookName || 'Release name'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
        </div>

        {/* Language */}
        <div>
          <label className="block text-gray-700 font-medium">
            Language profile
          </label>
          <select
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          >
            <option value="">Choose…</option>
            <option value="es_de">ES-DE</option>
            <option value="de_en">DE-EN</option>
            <option value="es_en">ES-EN</option>
            <option value="nl_en">NL-EN</option>
            <option value="es_ru">ES-RU</option>
            <option value="de_ru">DE-RU</option>
            <option value="es_it">ES-IT</option>
            <option value="de_it">DE-IT</option>
            <option value="es_fr">ES-FR</option>
            <option value="de_fr">DE-FR</option>
            <option value="de_tr">DE-TR</option>
            <option value="es_tr">ES-TR</option>
            <option value="de_pl">DE-PL</option>
            <option value="es_pl">ES-PL</option>
            <option value="de">DE</option>
            <option value="fr">FR</option>
            <option value="it">IT</option>
            <option value="es">ES</option>
            <option value="ru">RU</option>
            <option value="nl">NL</option>
            <option value="tr">TR</option>
            <option value="pl">PL</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Pre-filled from <code>?plang=</code> when available.
          </p>
        </div>

        {/* Release date */}
        <div>
          <label className="block text-gray-700 font-medium">
            Release date
          </label>
          <input
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-gray-700 font-medium">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="Add release notes or context for this publication"
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">
            This text populates the release description.
          </p>
        </div>

        {/* Cover */}
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Cover (PDF)
          </label>

          <input
            type="file"
            accept="application/pdf"
            onChange={handleCoverFileChange}
            className="w-full p-2 border border-gray-300 rounded mt-2"
          />

          {(coverPdfPreviewUrl ||
            (coverUrl && coverUrl.toLowerCase().endsWith('.pdf'))) && (
            <div className="mt-2 text-center">
              <span className="inline-block px-2 py-1 text-xs rounded bg-gray-100 border">
                Cover PDF selected
              </span>
              {coverPdfPreviewUrl && (
                <div className="mt-1">
                  <a
                    href={coverPdfPreviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline text-xs"
                  >
                    Open selected cover (PDF)
                  </a>
                </div>
              )}
              {coverUrl && coverUrl.toLowerCase().endsWith('.pdf') && (
                <div className="mt-1">
                  <a
                    href={coverUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline break-all text-xs"
                  >
                    {coverUrl}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* PDF */}
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Release PDF
          </label>

          <input
            type="file"
            accept="application/pdf"
            onChange={handlePdfFileChange}
            className="w-full p-2 border border-gray-300 rounded mt-2"
          />
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-center items-center gap-4">
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            disabled={loading}
          >
            {loading ? 'Creating…' : 'Create Release'}
          </button>

          <Link
            to={`/book-releases/${bookId}?plang=${languageCode || ''}`}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            View Releases
          </Link>
        </div>
      </form>
    </div>
  );
}

export default CreateBookRelease;
